import React, { useState, useEffect } from 'react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    placeholderSrc?: string;
    className?: string;
    imgClassName?: string;
}

/**
 * Enterprise-grade Lazy Image component.
 * Supports native lazy loading, blur-up placeholders, and progressive loading.
 * 
 * @param className - Applied to the wrapper div (dimensions, margins, positioning)
 * @param imgClassName - Applied directly to the img element (object-fit, etc.)
 */
const LazyImage: React.FC<LazyImageProps> = ({
    src,
    alt,
    placeholderSrc,
    className = '',
    imgClassName = '',
    ...props
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setIsLoaded(false);
        setHasError(false);

        const img = new Image();
        img.src = src;

        img.onload = () => {
            setIsLoaded(true);
        };

        img.onerror = (e) => {
            setHasError(true);
            if (props.onError) {
                props.onError(e as unknown as React.SyntheticEvent<HTMLImageElement, Event>);
            }
        };

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [src, props.onError]);

    if (hasError) {
        return (
            <div
                className={`flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 ${className}`}
                role="img"
                aria-label={`Failed to load image: ${alt}`}
            >
                <span className="text-xs">Image Error</span>
            </div>
        );
    }

    return (
        <div className={`relative overflow-hidden ${className}`}>
            {/* Placeholder Image (Blur Up) */}
            {placeholderSrc && (
                <img
                    src={placeholderSrc}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full object-cover blur-lg transition-opacity duration-500 ease-in-out ${isLoaded ? 'opacity-0' : 'opacity-100'
                        }`}
                />
            )}

            {/* Main Image */}
            <img
                src={src}
                alt={alt}
                loading="lazy"
                className={`block w-full h-full transition-opacity duration-500 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'
                    } ${imgClassName}`}
                {...props}
            />
        </div>
    );
};

export default LazyImage;
